
import React from "react";
import { Trash2 } from "lucide-react";
import { CalendarEvent } from "@/hooks/calendar/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "react-router-dom";

interface EventItemProps {
  event: CalendarEvent;
  isInPast: boolean;
  onDeleteEvent?: (event: CalendarEvent) => Promise<void>;
  onEventClick?: (e: React.MouseEvent) => void;
}

export const EventItem: React.FC<EventItemProps> = ({
  event,
  isInPast,
  onDeleteEvent,
  onEventClick = () => {} // Valore predefinito come funzione vuota
}) => {
  const getEventStyle = (event: CalendarEvent) => {
    // Per sessioni cliente, nessun background ma solo bordo colorato
    if (event.type === "session" && event.clientId) {
      let border = 'none';
      let opacity = '1';
      
      if (event.status === 'pending') {
        border = '2px dashed #f59e0b'; // Bordo tratteggiato giallo per pending
        opacity = '0.8';
      } else if (event.status === 'rejected') {
        border = '2px solid #ef4444'; // Bordo rosso per rejected
        opacity = '0.7';
      }
      
      return {
        background: 'transparent',
        opacity,
        border
      };
    }
    
    // Stile per eventi non-session (mantiene il comportamento originale)
    let backgroundColor = event.colorCode || '#3b82f6';
    let opacity = '1';
    let border = 'none';
    
    if (event.status === 'pending') {
      backgroundColor = '#f59e0b';
      opacity = '0.8';
      border = '2px dashed #d97706';
    } else if (event.status === 'rejected') {
      backgroundColor = '#ef4444';
      opacity = '0.7';
      border = 'none';
    }
    
    return {
      background: backgroundColor,
      opacity,
      border
    };
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteEvent) {
      console.log("EventItem - Click sul pulsante di eliminazione per l'evento:", event);
      try {
        await onDeleteEvent(event);
      } catch (error) {
        console.error("EventItem - Errore durante l'eliminazione dell'evento:", error);
      }
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  // Determina il colore del bordo dell'avatar basato sullo stato dell'evento e colore cliente
  const getAvatarBorderStyle = (event: CalendarEvent) => {
    if (event.type !== 'session' || !event.clientId) return {};
    
    let borderColor = event.colorCode || '#3b82f6';
    
    if (event.status === 'pending') {
      borderColor = '#f59e0b'; // Giallo per pending
    } else if (event.status === 'rejected') {
      borderColor = '#ef4444'; // Rosso per rejected
    }
    
    return {
      borderColor,
      borderWidth: '4px',
      borderStyle: 'solid'
    };
  };

  return (
    <div 
      className={` rounded-lg mb-1.5 transition-all
          ${isInPast ? 'opacity-75' : ''}
          group relative`} 
      style={getEventStyle(event)}
      onClick={onEventClick}
    >
      <div className="flex flex-col gap-1">
        {event.type !== 'session' && event.title && (
          <div className="p-1.5 sm:p-2 text-xs font-semibold mb-1 truncate max-w-full">
            {event.title}
          </div>
        )}
        
        {event.clientId && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1.5">
              <Avatar 
                className="h-8 w-8"
                style={getAvatarBorderStyle(event)}
              >
                <AvatarImage 
                  src={event.clientImage || ""} 
                  alt={event.clientName || ""}
                  className={!event.clientImage ? "hidden" : ""}
                />
                <AvatarFallback className="text-[10px]">
                  {getInitials(event.clientName)}
                </AvatarFallback>
              </Avatar>
              
              <Link 
                to={`/admin/clients/${event.clientId}`} 
                onClick={e => e.stopPropagation()}
                className="text-xs font-medium truncate hover:underline max-w-[100px]"
                style={{ color: event.type === 'session' ? (event.colorCode || '#3b82f6') : 'white' }}
              >
                {event.clientName}
              </Link>
            </div>
          </div>
        )}
      </div>
      
      {onDeleteEvent && (
        <button 
          className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 p-0.5 rounded-full bg-red-500/20 hover:bg-red-500 text-white transition-all"
          onClick={handleDeleteClick}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};
